import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import PlanSelector from './components/PlanSelector';
import OrderSummary from './components/OrderSummary';
import type { Plan } from './components/PlanSelector';
import type { Engagement } from './components/PlanSelector';
import { VITRINE_PLANS } from './components/PlanSelector';

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;

export default function PaiementPage() {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(VITRINE_PLANS[1]);
  const [engagement, setEngagement] = useState<Engagement>('sans');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Paiement Abonnement – UzunDigital';
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.href = `${siteUrl}/paiement`;
    } else {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      canonical.href = `${siteUrl}/paiement`;
      document.head.appendChild(canonical);
    }

    // WebPage JSON-LD
    const pageScript = document.createElement('script');
    pageScript.type = 'application/ld+json';
    pageScript.id = 'ld-paiement-page';
    pageScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Paiement Abonnement – UzunDigital',
      url: `${siteUrl}/paiement`,
      description: 'Souscrivez à votre abonnement de création de site internet professionnel. Vitrine à partir de 69€/mois, e-commerce dès 149€/mois. Sans engagement, annulable à tout moment.',
      inLanguage: 'fr-FR',
      isPartOf: { '@type': 'WebSite', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'Nos Offres', item: `${siteUrl}/offres` },
          { '@type': 'ListItem', position: 3, name: 'Paiement', item: `${siteUrl}/paiement` },
        ],
      },
    });

    // Service + Offers JSON-LD
    const serviceScript = document.createElement('script');
    serviceScript.type = 'application/ld+json';
    serviceScript.id = 'ld-paiement-service';
    serviceScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Création de site internet – Abonnement mensuel',
      url: `${siteUrl}/paiement`,
      provider: {
        '@type': 'ProfessionalService',
        name: 'UzunDigital',
        url: siteUrl,
        email: 'uzundigital71@gmail.com',
        telephone: '+33651872435',
      },
      description: 'Abonnement mensuel pour la création et la maintenance de votre site internet professionnel. Livraison en 7 jours maximum, sans engagement, annulable à tout moment.',
      areaServed: 'FR',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Abonnements site internet',
        itemListElement: [
          {
            '@type': 'Offer',
            name: 'Site Vitrine – Essentiel',
            price: '69',
            priceCurrency: 'EUR',
            description: 'Site vitrine essentiel à 69€/mois, sans engagement, hébergement inclus.',
            eligibleRegion: { '@type': 'Country', name: 'FR' },
          },
          {
            '@type': 'Offer',
            name: 'Site Vitrine – Pro',
            price: '89',
            priceCurrency: 'EUR',
            description: 'Site vitrine professionnel à 89€/mois avec fonctionnalités avancées.',
            eligibleRegion: { '@type': 'Country', name: 'FR' },
          },
          {
            '@type': 'Offer',
            name: 'Site E-commerce – Starter',
            price: '149',
            priceCurrency: 'EUR',
            description: 'Boutique en ligne starter à 149€/mois avec paiement sécurisé intégré.',
            eligibleRegion: { '@type': 'Country', name: 'FR' },
          },
        ],
      },
    });

    document.head.appendChild(pageScript);
    document.head.appendChild(serviceScript);

    return () => {
      ['ld-paiement-page', 'ld-paiement-service'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  const handleCheckout = async (email: string) => {
    if (!selectedPlan) return;
    setLoading(true);
    setError(null);

    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/create-subscription-checkout`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            planName: selectedPlan.name,
            planType: selectedPlan.type,
            engagement,
            price: selectedPlan.prices[engagement],
            successUrl: `${siteUrl}/paiement/success`,
            cancelUrl: `${siteUrl}/paiement`,
            customerEmail: email,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Une erreur est survenue. Veuillez réessayer.');
      }

      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 sm:pt-32 pb-10 sm:pb-14 px-4 sm:px-6 overflow-hidden bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs text-slate-400 font-semibold mb-6">
            <Link to="/" className="hover:text-emerald-500 transition-colors cursor-pointer">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/offres" className="hover:text-emerald-500 transition-colors cursor-pointer">Nos Offres</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-600">Paiement</span>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-xs font-bold mb-3">
                <i className="ri-secure-payment-line text-sm"></i>
                <span>PAIEMENT SÉCURISÉ</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 leading-tight">
                Choisissez votre abonnement
              </h1>
              <p className="text-sm text-slate-500 mt-1.5 max-w-xl">
                Sélectionnez le plan adapté à votre projet. <strong className="text-slate-700">Annulable à tout moment</strong>, sans frais cachés.
              </p>
            </div>

            {/* Reassurances */}
            <div className="hidden sm:flex items-center gap-4 text-xs text-slate-500 font-semibold flex-shrink-0">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-emerald-500"></i>
                </div>
                <span>100% sécurisé</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-refresh-line text-emerald-500"></i>
                </div>
                <span>Résiliation libre</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-stripe-line text-slate-400"></i>
                </div>
                <span>Propulsé par Stripe</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {error && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-2xl p-4">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-500"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-red-700">Une erreur est survenue</p>
                <p className="text-xs text-red-600 mt-0.5">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 items-start">
            {/* Left – Plan selector */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 sm:p-7">
                <h2 className="text-base font-black text-slate-900 mb-5">
                  1. Sélectionnez votre plan
                </h2>
                <PlanSelector
                  selectedPlan={selectedPlan}
                  onSelectPlan={setSelectedPlan}
                  engagement={engagement}
                  onEngagementChange={setEngagement}
                />
              </div>

              {/* Info box */}
              <div className="mt-4 bg-white rounded-2xl border-2 border-slate-100 p-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-100 flex-shrink-0">
                    <i className="ri-question-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900 mb-1">Comment ça fonctionne ?</p>
                    <ol className="space-y-1.5 text-xs text-slate-600 leading-relaxed list-decimal list-inside">
                      <li>Sélectionnez votre plan et renseignez votre e-mail</li>
                      <li>Vous êtes redirigé vers Stripe pour le paiement sécurisé</li>
                      <li>Dès la confirmation, nous démarrons votre projet</li>
                      <li>Votre site est livré en <strong className="text-slate-800">7 jours maximum</strong></li>
                    </ol>
                    <p className="mt-2 text-xs text-slate-500">
                      Une question ? <Link to="/demande-devis" className="text-emerald-600 font-bold hover:underline cursor-pointer">Demandez un devis gratuit</Link> ou appelez-nous.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right – Order summary */}
            <div className="lg:col-span-2">
              {selectedPlan ? (
                <div>
                  <h2 className="text-base font-black text-slate-900 mb-4 hidden lg:block">
                    2. Confirmez et payez
                  </h2>
                  <OrderSummary
                    plan={selectedPlan}
                    engagement={engagement}
                    onCheckout={handleCheckout}
                    loading={loading}
                  />
                </div>
              ) : (
                <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-8 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-slate-100 mx-auto mb-3">
                    <i className="ri-cursor-line text-2xl text-slate-400"></i>
                  </div>
                  <p className="text-sm font-bold text-slate-700">Sélectionnez un plan</p>
                  <p className="text-xs text-slate-400 mt-1">Le récapitulatif apparaîtra ici</p>
                </div>
              )}
            </div>
          </div>

          {/* Trust badges bottom */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
              {[
                { icon: 'ri-lock-line', label: 'Paiement chiffré SSL' },
                { icon: 'ri-stripe-line', label: 'Sécurisé par Stripe' },
                { icon: 'ri-refresh-line', label: 'Annulable à tout moment' },
                { icon: 'ri-timer-flash-line', label: 'Livraison en 7 jours max' },
                { icon: 'ri-customer-service-2-line', label: 'Support inclus' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-500">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-emerald-500 text-base`}></i>
                  </div>
                  <span className="text-xs font-semibold">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
